import type { Data as PlotlyData, ScatterData, ScattermapData } from "plotly.js";

// plotly.js 4 dropped the DefinitelyTyped names this codebase was written against; the nearest
// schema-generated equivalents are aliased here so call sites keep a stable vocabulary...
export type PlotData = ScatterData;
export type ScatterLine = NonNullable<ScatterData["line"]>;
export type ScattermapTraceProperties = Partial<ScattermapData> & Record<string, unknown>;

// The retired DefinitelyTyped `Data` behaved as a permissive property bag, which the chart
// components lean on for cross-trace property access; v4's schema-generated union does not...
export type Data = PlotlyData & { [key: string]: any };

const legacyTraceTypes = {
    scattermapbox: "scattermap",
    choroplethmapbox: "choroplethmap",
    densitymapbox: "densitymap",
};

type LegacyTrace = { type?: string; subplot?: string; [key: string]: unknown };

const translateTrace = (trace: LegacyTrace): LegacyTrace => {
    if (!trace) return trace;

    const type = legacyTraceTypes[trace.type];
    const subplot = typeof trace.subplot === "string" ? trace.subplot.replace(/^mapbox(?=\d*$)/, "map") : undefined;
    const subplotChanged = subplot !== undefined && subplot !== trace.subplot;

    return type || subplotChanged
        ? { ...trace, ...(type ? { type } : {}), ...(subplotChanged ? { subplot } : {}) }
        : trace;
};

const translateLayout = <T extends Record<string, any>>(layout: T): T => {
    const legacyKeys = Object.keys(layout ?? {}).filter(key => /^mapbox\d*$/.test(key));
    if (legacyKeys.length === 0) return layout;

    return legacyKeys.reduce((acc, key) => {
        const { [key]: legacySubplot, ...rest } = acc;
        // The maplibre-based subplots are token-free, so `accesstoken` has no target field...
        const { accesstoken, ...subplot } = legacySubplot ?? {};
        const target = key.replace("mapbox", "map");
        return { ...rest, [target]: target in rest ? rest[target] : subplot } as T;
    }, layout);
};

/**
 * Rewrites the Mapbox-based trace types/subplots that plotly.js 4 removed (`scattermapbox`,
 * `layout.mapbox`, ...) to their maplibre-based successors (`scattermap`, `layout.map`, ...), so
 * that figures authored against older plotly versions keep rendering.  Figures that need no
 * translation are returned by reference - consumers gate state updates on (deep) equality, and
 * fresh references would churn their effects...
 */
export const translateLegacyMapboxFigure = <T extends { data?: unknown[]; layout?: unknown }>(figure: T): T => {
    if (!figure) return figure;

    const data = figure.data?.map(trace => translateTrace(trace as LegacyTrace));
    const layout = translateLayout(figure.layout as Record<string, any>);
    const dataChanged = data?.some((trace, index) => trace !== figure.data[index]) ?? false;

    return dataChanged || layout !== figure.layout
        ? { ...figure, ...(dataChanged ? { data } : {}), ...(layout !== figure.layout ? { layout } : {}) }
        : figure;
};
